// Ch94 · ENGAGE — grouping repeats, and saying WHY in plain words.
//
// 🔴 THE DEFECT THIS IS SHAPED AROUND, measured live by the Ch94 Director on
// Grant's queue: 99 conversations to reply to, 0 ever replied, and THE SAME
// POST APPEARING 10+ TIMES. A list that shows one post ten times is not a list
// of 99 things to do — it is a much shorter list wearing a bigger number, and
// the number is what makes it feel hopeless.
//
// So repeats collapse into ONE row that says how many times it was seen. The
// count is kept rather than discarded: a post the scorer keeps re-surfacing is
// evidence about the post, not noise.
//
// No React — every rule below is testable without a DOM.

export interface EngageLead {
  id: string;
  pipeline_state?: string;
  source_url: string;
  score: number;
  verdict: string;
  created_at: string;
  body_preview: string | null;
  context_snippet: string | null;
  matched_signals: string[] | { signals?: unknown } | null;
  raw_payload: Record<string, unknown> | null;
  digest_item: { suggested_reply_snapshot: string } | null;
  latest_revision: { final_text: string; action: string } | null;
}

export interface EngageGroup {
  /** The lead we act on — the newest, so his reply lands on the live thread. */
  lead: EngageLead;
  /** Every lead folded into this row, newest first. */
  repeats: EngageLead[];
  /** The stable key: the post, not the row. */
  permalink: string;
}

/**
 * The identity of a POST, not of a queue row.
 *
 * Normalised so the same thread cannot arrive as several different keys:
 * query strings (`?utm_source=…`), trailing slashes and case differences are
 * all the same post. Reddit permalinks are case-insensitive in practice and
 * the scorer has clearly been re-adding them.
 */
export function permalinkKey(lead: EngageLead): string {
  const raw = (lead.source_url || "").trim();
  if (!raw) {
    // No permalink: fall back to the row's own id so it stays its OWN group
    // rather than merging with every other permalink-less row. Collapsing
    // unrelated posts would be worse than showing a repeat.
    return `lead:${lead.id}`;
  }
  try {
    const url = new URL(raw);
    const path = url.pathname.replace(/\/+$/, "").toLowerCase();
    return `${url.host.toLowerCase()}${path}`;
  } catch {
    return raw.replace(/[?#].*$/, "").replace(/\/+$/, "").toLowerCase();
  }
}

const newestFirst = (a: EngageLead, b: EngageLead): number =>
  Date.parse(b.created_at || "") - Date.parse(a.created_at || "") || 0;

/** Collapse repeats of the same post into one row each. */
export function groupByPost(leads: EngageLead[]): EngageGroup[] {
  const byPost = new Map<string, EngageLead[]>();
  leads.forEach((lead) => {
    const key = permalinkKey(lead);
    const bucket = byPost.get(key);
    if (bucket) bucket.push(lead);
    else byPost.set(key, [lead]);
  });

  return Array.from(byPost.entries()).map(([permalink, members]) => {
    const repeats = [...members].sort(newestFirst);
    return { permalink, repeats, lead: repeats[0] };
  });
}

/** "4 repeats grouped", or null when it appeared once. */
export function repeatsLabel(group: EngageGroup): string | null {
  return group.repeats.length > 1
    ? `${group.repeats.length} repeats grouped`
    : null;
}

/**
 * Turn the scorer's signal names into Grant's words.
 *
 * 🔴 AN UNKNOWN SIGNAL IS SHOWN, NOT DROPPED. The signal vocabulary lives on
 * the Workbench and can grow without any change here — the same open-ended
 * shape as `template`. Hiding a signal we have no phrase for would quietly
 * remove the only explanation a row has; showing the raw name at least lets
 * him see what the scorer reacted to.
 */
const SIGNAL_WORDS: Record<string, string> = {
  mover: "they're moving soon",
  no_car: "no car — wants somewhere walkable",
  budget: "they named a budget",
  neighborhood: "asking which neighbourhood",
  locator: "asking about locators",
  luxury: "looking at higher-end places",
  urgent: "they need it quickly",
};

export interface WhyItsHere {
  /** Plain-word phrases, in Grant's language. */
  plain: string[];
  /** Signals we had no phrase for — shown raw rather than hidden. */
  untranslated: string[];
  /** The scorer's own line, kept for the disclosure. */
  raw: string | null;
}

/**
 * The scorer's signals, however the wire chose to wrap them.
 *
 * ὓ4 MEASURED IN PRODUCTION 2026-09-20, 100 of 100 live rows: the queue returns
 * `matched_signals` as an OBJECT — `{"signals": ["+20 intent: roommate", ...]}` —
 * while this file's own type said `string[] | null`. `?? []` does not catch an
 * object, so `.map` threw and the Engage tab rendered a WHITE SCREEN the first
 * time it was ever shown real data.
 *
 * It passed 2,065 unit tests and 52 Playwright specs because every fixture
 * carries `"matched_signals": []` — the declared shape, not the served one. A
 * fixture that agrees with the type instead of with the server proves only that
 * the type is self-consistent.
 *
 * So: accept BOTH shapes and never throw on a third. An unreadable signals blob
 * costs one explanatory line on one row; a thrown TypeError costs the whole page.
 */
export function signalList(raw: EngageLead["matched_signals"]): string[] {
  if (Array.isArray(raw)) return raw as string[];
  const nested = (raw as { signals?: unknown } | null)?.signals;
  return Array.isArray(nested) ? (nested as string[]) : [];
}

export function whyItsHere(lead: EngageLead): WhyItsHere {
  const signals = signalList(lead.matched_signals).map((s) => (String(s ?? "")).trim()).filter(Boolean);
  const plain: string[] = [];
  const untranslated: string[] = [];

  signals.forEach((signal) => {
    const phrase = SIGNAL_WORDS[signal.toLowerCase()];
    if (phrase) plain.push(phrase);
    else untranslated.push(signal);
  });

  return {
    plain,
    untranslated,
    raw: lead.context_snippet?.trim() || null,
  };
}

/**
 * The reply to put in the box.
 *
 * 🔴 RETURNS null WHEN THE SOURCE GAVE US NOTHING, AND THE UI MUST SAY SO.
 * Every "Suggested reply" box on Grant's live queue rendered EMPTY on
 * 2026-09-19. Reading the component settled where it does NOT come from:
 * `RedditAnswerDesk.tsx:7` seeds `latest_revision?.final_text ??
 * digest_item?.suggested_reply_snapshot ?? ""` with nullish coalescing, so no
 * path there discards a non-empty draft — the emptiness arrives from upstream.
 *
 * The packet is explicit: if the source never fills them, REPORT IT, DO NOT
 * FAKE THEM. So this distinguishes "he has edited one" from "the machine wrote
 * one" from "nothing came", and never invents text to fill a box.
 */
export type DraftSource = "his_edit" | "machine" | "none";

export function draftReply(lead: EngageLead): { text: string; source: DraftSource } {
  const edited = lead.latest_revision?.final_text?.trim();
  if (edited) return { text: edited, source: "his_edit" };

  const machine = lead.digest_item?.suggested_reply_snapshot?.trim();
  if (machine) return { text: machine, source: "machine" };

  return { text: "", source: "none" };
}

export type EngageSort = "newest" | "hottest";

export function sortGroups(groups: EngageGroup[], sort: EngageSort): EngageGroup[] {
  const copy = [...groups];
  if (sort === "hottest") {
    copy.sort((a, b) => b.lead.score - a.lead.score || newestFirst(a.lead, b.lead));
  } else {
    copy.sort((a, b) => newestFirst(a.lead, b.lead));
  }
  return copy;
}

/** Match the Workbench redditAnswerDesk writer's pipeline-to-queue mapping. */
export function engageQueueState(lead: EngageLead): "needs_reply" | "replied" | "dismissed" | "unknown" {
  const state = lead.pipeline_state;
  if (["queued", "sent_in_digest", "opened", "clicked_source", "clicked_copy", "needs_reply"].includes(state ?? "")) return "needs_reply";
  if (["replied_confirmed", "engaged_inferred", "replied"].includes(state ?? "")) return "replied";
  if (state === "dismissed") return "dismissed";
  if (state) return "unknown";
  // Older screen fixtures omit pipeline_state; preserve their revision evidence.
  return lead.latest_revision?.action === "marked_replied" ? "replied"
    : lead.latest_revision?.action === "dismissed" ? "dismissed" : "needs_reply";
}
